import { useState, useEffect, useCallback } from 'react';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const BASE = '/spring/hospital/api';

async function apiFetch(path, opts = {}) {
  const res = await fetch(BASE + path, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    ...opts,
  });
  return res.json().catch(() => ({}));
}

const TIME_SLOTS = [];
for (let h = 8; h < 19; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2,'0')}:00`);
  TIME_SLOTS.push(`${String(h).padStart(2,'0')}:30`);
}

const TABS = ['Appointments', 'Treatments', 'Lab Reports', 'Invoices', 'IPD', 'OPD'];

function StatusBadge({ status, map }) {
  const cfg = map[status?.toLowerCase()] || { color: '#888', bg: '#f0f0f0' };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, borderRadius: 6,
      padding: '2px 9px', fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>
      {status || '—'}
    </span>
  );
}

const APPT_STATUS = {
  scheduled: { color: C, bg: LIGHT },
  completed:  { color: '#27ae60', bg: '#e6f4ea' },
  cancelled:  { color: '#888', bg: '#f0f0f0' },
  'no-show':  { color: '#c5221f', bg: '#fce8e6' },
};

const LAB_STATUS = {
  pending:   { color: '#e67e22', bg: '#fef9e7' },
  completed: { color: '#27ae60', bg: '#e6f4ea' },
};

const INV_STATUS = {
  paid:    { color: '#27ae60', bg: '#e6f4ea' },
  pending: { color: '#e67e22', bg: '#fef9e7' },
};

const OPD_STATUS = {
  completed: { color: '#27ae60', bg: '#e6f4ea' },
  pending:   { color: '#e67e22', bg: '#fef9e7' },
  cancelled: { color: '#888', bg: '#f0f0f0' },
};

const TH = ({ children }) => (
  <th style={{ padding: '10px 14px', textAlign: 'left', color: DARK,
    fontWeight: 700, fontSize: 12, background: LIGHT }}>{children}</th>
);

const TD = ({ children, style }) => (
  <td style={{ padding: '10px 14px', fontSize: 13, color: '#3c4043', ...style }}>{children}</td>
);

function EmptyState({ msg }) {
  return (
    <div style={{ padding: '48px 20px', textAlign: 'center', color: '#9aa0a6', fontSize: 14 }}>
      {msg}
    </div>
  );
}

function Spinner() {
  return (
    <>
      <style>{`@keyframes _hspin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
        <div style={{ width: 38, height: 38, borderRadius: '50%',
          border: `4px solid ${LIGHT}`, borderTopColor: C,
          animation: '_hspin .7s linear infinite' }} />
      </div>
    </>
  );
}

function AppointmentsTab({ data, onBook }) {
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button onClick={onBook}
          style={{ background: C, color: '#fff', border: 'none', borderRadius: 8,
            padding: '9px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
          + Book Appointment
        </button>
      </div>
      {!data.length ? <EmptyState msg="No appointments yet" /> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>{['Date', 'Time', 'Doctor', 'Type', 'Status'].map(h => <TH key={h}>{h}</TH>)}</tr>
          </thead>
          <tbody>
            {data.map((a, i) => (
              <tr key={a.id || i} style={{ borderTop: '1px solid #f1f3f4',
                background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                <TD>{a.appt_date || a.date || '—'}</TD>
                <TD>{a.appt_time || a.time || '—'}</TD>
                <TD style={{ fontWeight: 600, color: DARK }}>{a.doctor_name || a.doctor || '—'}</TD>
                <TD>{a.type || '—'}</TD>
                <TD><StatusBadge status={a.status} map={APPT_STATUS} /></TD>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function BookAppointmentModal({ ownersid, onClose, onBooked }) {
  const [doctors, setDoctors]         = useState([]);
  const [specializations, setSpecs]   = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  const [form, setForm] = useState({
    specialization: '',
    doctorId: '',
    apptDate: '',
    apptTime: '',
    type: 'Consultation',
    notes: '',
  });

  useEffect(() => {
    if (!ownersid) return;
    apiFetch(`/public/${ownersid}/specializations`).then(d => {
      setSpecs(Array.isArray(d) ? d.map(r => r.specialization || r).filter(Boolean) : []);
    });
    apiFetch(`/public/${ownersid}/doctors`).then(d => {
      setDoctors(Array.isArray(d) ? d : []);
    });
  }, [ownersid]);

  useEffect(() => {
    if (form.specialization) {
      apiFetch(`/public/${ownersid}/doctors?specialization=${encodeURIComponent(form.specialization)}`).then(d => {
        setDoctors(Array.isArray(d) ? d : []);
      });
    } else {
      apiFetch(`/public/${ownersid}/doctors`).then(d => {
        setDoctors(Array.isArray(d) ? d : []);
      });
    }
    setForm(f => ({ ...f, doctorId: '', apptTime: '' }));
    setBookedSlots([]);
  }, [form.specialization, ownersid]);

  useEffect(() => {
    if (!form.doctorId || !form.apptDate) { setBookedSlots([]); return; }
    setSlotsLoading(true);
    apiFetch(`/public/${ownersid}/available-slots?doctorId=${form.doctorId}&date=${form.apptDate}`)
      .then(d => {
        setBookedSlots(Array.isArray(d?.bookedTimes) ? d.bookedTimes : []);
        setSlotsLoading(false);
      })
      .catch(() => setSlotsLoading(false));
  }, [form.doctorId, form.apptDate, ownersid]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const today = new Date().toISOString().split('T')[0];

  const handleSubmit = async () => {
    setError('');
    if (!form.doctorId || !form.apptDate || !form.apptTime)
      return setError('Please select a doctor, date, and time slot.');
    setSaving(true);
    const res = await apiFetch('/patient-portal/book-appointment', {
      method: 'POST',
      body: JSON.stringify({
        doctorId: form.doctorId,
        apptDate: form.apptDate,
        apptTime: form.apptTime,
        type: form.type,
        notes: form.notes,
      }),
    });
    setSaving(false);
    if (res.error) return setError(res.error);
    setSuccess('Appointment booked successfully!');
    setTimeout(() => { onBooked(); onClose(); }, 1500);
  };

  const filteredDoctors = doctors;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520,
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,.25)' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e8eaed',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: DARK }}>Book Appointment</div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9aa0a6' }}>
            &times;
          </button>
        </div>

        <div style={{ padding: '20px 22px' }}>
          {success ? (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>&#10003;</div>
              <div style={{ color: '#27ae60', fontWeight: 700, fontSize: 16 }}>{success}</div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: '#5f6368', fontWeight: 600 }}>Specialization (optional)</label>
                <select value={form.specialization} onChange={e => set('specialization', e.target.value)}
                  style={{ width: '100%', marginTop: 4, padding: '9px 10px', borderRadius: 7,
                    border: '1px solid #dadce0', fontSize: 13, background: '#fff' }}>
                  <option value="">All specializations</option>
                  {specializations.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: '#5f6368', fontWeight: 600 }}>Doctor *</label>
                <select value={form.doctorId}
                  onChange={e => set('doctorId', e.target.value)}
                  style={{ width: '100%', marginTop: 4, padding: '9px 10px', borderRadius: 7,
                    border: '1px solid #dadce0', fontSize: 13, background: '#fff' }}>
                  <option value="">Select doctor</option>
                  {filteredDoctors.map(d => (
                    <option key={d.id} value={d.id}>
                      Dr. {d.name}{d.specialization ? ` — ${d.specialization}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: '#5f6368', fontWeight: 600 }}>Date *</label>
                <input type="date" value={form.apptDate} min={today}
                  onChange={e => { set('apptDate', e.target.value); set('apptTime', ''); }}
                  style={{ width: '100%', marginTop: 4, padding: '9px 10px', borderRadius: 7,
                    border: '1px solid #dadce0', fontSize: 13, boxSizing: 'border-box' }} />
              </div>

              {form.doctorId && form.apptDate && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, color: '#5f6368', fontWeight: 600, display: 'block', marginBottom: 8 }}>
                    Time Slot *
                  </label>
                  {slotsLoading ? (
                    <div style={{ color: '#9aa0a6', fontSize: 13 }}>Loading slots...</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                      {TIME_SLOTS.map(slot => {
                        const booked = bookedSlots.includes(slot);
                        const selected = form.apptTime === slot;
                        return (
                          <button key={slot} disabled={booked}
                            onClick={() => !booked && set('apptTime', slot)}
                            style={{
                              padding: '7px 4px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                              cursor: booked ? 'not-allowed' : 'pointer',
                              border: selected ? `2px solid ${C}` : '1px solid #dadce0',
                              background: booked ? '#f1f3f4' : selected ? LIGHT : '#fff',
                              color: booked ? '#bdc1c6' : selected ? C : '#3c4043',
                              textDecoration: booked ? 'line-through' : 'none',
                            }}>
                            {slot}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: '#5f6368', fontWeight: 600 }}>Type</label>
                <select value={form.type} onChange={e => set('type', e.target.value)}
                  style={{ width: '100%', marginTop: 4, padding: '9px 10px', borderRadius: 7,
                    border: '1px solid #dadce0', fontSize: 13, background: '#fff' }}>
                  {['Consultation', 'Follow-up', 'Emergency', 'Routine Check-up', 'Procedure'].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 12, color: '#5f6368', fontWeight: 600 }}>Notes (optional)</label>
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                  rows={3} placeholder="Describe your symptoms or reason for visit..."
                  style={{ width: '100%', marginTop: 4, padding: '9px 10px', borderRadius: 7,
                    border: '1px solid #dadce0', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>

              {error && (
                <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 7,
                  padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={onClose}
                  style={{ flex: 1, padding: '11px 0', borderRadius: 8, border: '1px solid #dadce0',
                    background: '#fff', color: '#5f6368', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={handleSubmit} disabled={saving}
                  style={{ flex: 2, padding: '11px 0', borderRadius: 8, border: 'none',
                    background: saving ? '#9aa0a6' : C, color: '#fff',
                    fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Booking...' : 'Book Appointment'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PrescriptionRow({ med }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
      gap: 8, padding: '8px 0', borderBottom: '1px solid #f1f3f4', fontSize: 12 }}>
      <div><span style={{ color: '#9aa0a6' }}>Medicine</span><br />
        <strong>{med.medicine_name || med.name || '—'}</strong></div>
      <div><span style={{ color: '#9aa0a6' }}>Dosage</span><br />{med.dosage || '—'}</div>
      <div><span style={{ color: '#9aa0a6' }}>Frequency</span><br />{med.frequency || '—'}</div>
      <div><span style={{ color: '#9aa0a6' }}>Duration</span><br />{med.duration || '—'}</div>
      {med.instructions && (
        <div style={{ gridColumn: '1 / -1', color: '#5f6368', fontStyle: 'italic' }}>
          {med.instructions}
        </div>
      )}
    </div>
  );
}

function TreatmentCard({ t }) {
  const [open, setOpen] = useState(false);
  const meds = t.prescriptions || [];
  return (
    <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e8eaed',
      padding: 18, marginBottom: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))', gap: 12, marginBottom: 12 }}>
        <div><div style={{ fontSize: 11, color: '#9aa0a6' }}>Visit Date</div>
          <div style={{ fontWeight: 600, color: DARK }}>{t.visit_date || t.date || '—'}</div></div>
        <div><div style={{ fontSize: 11, color: '#9aa0a6' }}>Doctor</div>
          <div style={{ fontWeight: 600 }}>{t.doctor_name || t.doctor || '—'}</div></div>
        <div><div style={{ fontSize: 11, color: '#9aa0a6' }}>Diagnosis</div>
          <div>{t.diagnosis || '—'}</div></div>
        <div><div style={{ fontSize: 11, color: '#9aa0a6' }}>Follow-up Date</div>
          <div>{t.follow_up_date || t.followup_date || '—'}</div></div>
      </div>
      {t.treatment_notes && (
        <div style={{ fontSize: 13, color: '#5f6368', background: '#f9fafb',
          borderRadius: 7, padding: '10px 14px', marginBottom: 10 }}>
          {t.treatment_notes}
        </div>
      )}
      {meds.length > 0 && (
        <div>
          <button onClick={() => setOpen(o => !o)}
            style={{ background: 'none', border: 'none', color: C, cursor: 'pointer',
              fontSize: 13, fontWeight: 600, padding: 0 }}>
            {open ? '&#9662;' : '&#9658;'} Prescriptions ({meds.length})
          </button>
          {open && (
            <div style={{ marginTop: 10, paddingLeft: 8 }}>
              {meds.map((m, i) => <PrescriptionRow key={i} med={m} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TreatmentsTab({ data }) {
  if (!data.length) return <EmptyState msg="No treatment records yet" />;
  return <div>{data.map((t, i) => <TreatmentCard key={t.id || i} t={t} />)}</div>;
}

function LabReportsTab({ data }) {
  if (!data.length) return <EmptyState msg="No lab reports yet" />;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>{['Date', 'Test Name', 'Doctor', 'Result', 'Normal Range', 'Status'].map(h => <TH key={h}>{h}</TH>)}</tr>
      </thead>
      <tbody>
        {data.map((r, i) => (
          <tr key={r.id || i} style={{ borderTop: '1px solid #f1f3f4',
            background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
            <TD>{r.date || r.test_date || '—'}</TD>
            <TD style={{ fontWeight: 600 }}>{r.test_name || r.testName || '—'}</TD>
            <TD>{r.doctor_name || r.doctor || '—'}</TD>
            <TD>{r.result || '—'}</TD>
            <TD style={{ color: '#5f6368' }}>{r.normal_range || r.normalRange || '—'}</TD>
            <TD><StatusBadge status={r.status} map={LAB_STATUS} /></TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function printInvoice(inv) {
  const w = window.open('', '_blank', 'width=700,height=900');
  w.document.write(`<!DOCTYPE html><html><head><title>Invoice ${inv.invoice_no || ''}</title>
<style>body{font-family:Arial,sans-serif;padding:40px;color:#222}
h2{color:#0f4c75}table{width:100%;border-collapse:collapse;margin-top:20px}
th,td{padding:10px 14px;text-align:left;border:1px solid #e0e0e0}
th{background:#e8f4fd;color:#0f4c75}.total{font-size:18px;font-weight:700;color:#0f4c75}
</style></head><body>
<h2>Invoice</h2>
<table>
<tr><th>Invoice No</th><td>${inv.invoice_no || '—'}</td><th>Date</th><td>${inv.date || '—'}</td></tr>
<tr><th>Doctor</th><td>${inv.doctor_name || inv.doctor || '—'}</td><th>Status</th><td>${inv.status || '—'}</td></tr>
<tr><th>Total Amount</th><td class="total">${inv.total != null ? '&#8377;' + inv.total : '—'}</td>
<th>Paid Amount</th><td class="total">${inv.paid_amount != null ? '&#8377;' + inv.paid_amount : '—'}</td></tr>
</table>
<br/><p style="color:#9aa0a6;font-size:12px">Printed from patient portal</p>
<script>window.onload=()=>window.print();<\/script></body></html>`);
  w.document.close();
}

function InvoicesTab({ data }) {
  if (!data.length) return <EmptyState msg="No invoices yet" />;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>{['Date', 'Invoice No', 'Doctor', 'Total', 'Paid Amount', 'Status', ''].map(h => <TH key={h}>{h}</TH>)}</tr>
      </thead>
      <tbody>
        {data.map((inv, i) => (
          <tr key={inv.id || i} style={{ borderTop: '1px solid #f1f3f4',
            background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
            <TD>{inv.date || '—'}</TD>
            <TD style={{ fontWeight: 600, color: DARK }}>{inv.invoice_no || '—'}</TD>
            <TD>{inv.doctor_name || inv.doctor || '—'}</TD>
            <TD>{inv.total != null ? '₹' + inv.total : '—'}</TD>
            <TD>{inv.paid_amount != null ? '₹' + inv.paid_amount : '—'}</TD>
            <TD><StatusBadge status={inv.status} map={INV_STATUS} /></TD>
            <TD>
              <button onClick={() => printInvoice(inv)}
                style={{ fontSize: 11, padding: '4px 10px', background: LIGHT,
                  color: DARK, border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>
                Print
              </button>
            </TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function IPDTab({ data }) {
  if (!data.length) return <EmptyState msg="No IPD admissions yet" />;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>{['Admission No', 'Admitted', 'Discharged', 'Room', 'Diagnosis', 'Status', 'Total Charges'].map(h => <TH key={h}>{h}</TH>)}</tr>
      </thead>
      <tbody>
        {data.map((a, i) => (
          <tr key={a.id || i} style={{ borderTop: '1px solid #f1f3f4',
            background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
            <TD style={{ fontWeight: 600, color: DARK }}>{a.admission_no || '—'}</TD>
            <TD>{a.admitted || a.admission_date || '—'}</TD>
            <TD>{a.discharged || a.discharge_date || '—'}</TD>
            <TD>{[a.room_no, a.room_type].filter(Boolean).join(' ') || '—'}</TD>
            <TD>{a.diagnosis || '—'}</TD>
            <TD>
              <StatusBadge status={a.status}
                map={{ admitted: { color: C, bg: LIGHT }, discharged: { color: '#27ae60', bg: '#e6f4ea' } }} />
            </TD>
            <TD>{a.total_charges != null ? '₹' + a.total_charges : '—'}</TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OPDTab({ data }) {
  if (!data.length) return <EmptyState msg="No OPD visits yet" />;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>{['Date', 'Token #', 'Doctor', 'Chief Complaint', 'Diagnosis', 'Fee', 'Status'].map(h => <TH key={h}>{h}</TH>)}</tr>
      </thead>
      <tbody>
        {data.map((v, i) => (
          <tr key={v.id || i} style={{ borderTop: '1px solid #f1f3f4',
            background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
            <TD>{v.date || v.visit_date || '—'}</TD>
            <TD style={{ fontWeight: 600, color: DARK }}>{v.token_no || v.token || '—'}</TD>
            <TD>{v.doctor_name || v.doctor || '—'}</TD>
            <TD>{v.chief_complaint || v.complaint || '—'}</TD>
            <TD>{v.diagnosis || '—'}</TD>
            <TD>{v.fee != null ? '₹' + v.fee : '—'}</TD>
            <TD><StatusBadge status={v.status} map={OPD_STATUS} /></TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function HospitalPatientPortalApp({ user }) {
  const [profile, setProfile]     = useState(null);
  const [records, setRecords]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState(0);
  const [showBook, setShowBook]   = useState(false);

  const loadData = useCallback(() => {
    Promise.all([
      apiFetch('/patient-portal/me'),
      apiFetch('/patient-portal/records'),
    ]).then(([p, r]) => {
      setProfile(p);
      setRecords(r);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSignOut = async () => {
    await apiFetch('/patient-auth/logout', { method: 'POST' });
    window.location.href = '/spring/login';
  };

  const ownersid = user?.ownersid || profile?.ownersid || records?.hospitalInfo?.ownersid;

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ background: DARK, padding: '0 28px', height: 58,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,.25)' }}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 17, letterSpacing: 0.3 }}>
          {user?.businesstitle || 'Hospital'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{user?.username}</div>
            <div style={{ color: '#90c8f0', fontSize: 11 }}>Patient Portal</div>
          </div>
          <button onClick={handleSignOut}
            style={{ background: 'rgba(255,255,255,.15)', color: '#fff',
              border: '1px solid rgba(255,255,255,.3)', borderRadius: 7,
              padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            Sign Out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px' }}>
        {loading ? (
          <Spinner />
        ) : (
          <>
            {profile && (
              <div style={{ background: '#fff', borderRadius: 12, padding: '16px 22px',
                marginBottom: 20, border: '1px solid #e8eaed',
                boxShadow: '0 1px 6px rgba(0,0,0,.07)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: C,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 700, fontSize: 18 }}>
                    {(profile.name || user?.username || 'P')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: DARK }}>{profile.name || user?.username}</div>
                    <div style={{ fontSize: 12, color: '#9aa0a6' }}>Code: {profile.patient_code || '—'}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px,1fr))', gap: 12 }}>
                  {[
                    ['Date of Birth', profile.dob],
                    ['Gender', profile.gender],
                    ['Blood Group', profile.blood_group],
                    ['Phone', profile.phone],
                    ['Emergency Contact', profile.emergency_contact],
                  ].map(([label, val]) => (
                    <div key={label}>
                      <div style={{ fontSize: 11, color: '#9aa0a6', marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#3c4043' }}>{val || '—'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden',
              boxShadow: '0 1px 6px rgba(0,0,0,.07)', border: '1px solid #e8eaed' }}>
              <div style={{ display: 'flex', borderBottom: '2px solid #e8eaed', overflowX: 'auto' }}>
                {TABS.map((t, idx) => (
                  <button key={t} onClick={() => setTab(idx)}
                    style={{ padding: '14px 20px', background: 'none', border: 'none',
                      cursor: 'pointer', fontSize: 13, fontWeight: tab === idx ? 700 : 500,
                      color: tab === idx ? C : '#5f6368', whiteSpace: 'nowrap',
                      borderBottom: tab === idx ? `2px solid ${C}` : '2px solid transparent',
                      marginBottom: -2 }}>
                    {t}
                  </button>
                ))}
              </div>

              <div style={{ padding: 20 }}>
                {!records ? (
                  <EmptyState msg="Could not load records." />
                ) : tab === 0 ? (
                  <AppointmentsTab data={records.appointments || []} onBook={() => setShowBook(true)} />
                ) : tab === 1 ? (
                  <TreatmentsTab data={records.treatments || []} />
                ) : tab === 2 ? (
                  <LabReportsTab data={records.labReports || []} />
                ) : tab === 3 ? (
                  <InvoicesTab data={records.invoices || []} />
                ) : tab === 4 ? (
                  <IPDTab data={records.admissions || []} />
                ) : (
                  <OPDTab data={records.opdVisits || []} />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {showBook && ownersid && (
        <BookAppointmentModal
          ownersid={ownersid}
          onClose={() => setShowBook(false)}
          onBooked={loadData}
        />
      )}
    </div>
  );
}
